import React, { useState } from 'react';
import { GraduationCap, ArrowLeft, Send, Sparkles, CalendarPlus, BookOpen, Brain, ListChecks } from 'lucide-react';
import { SubjectWithThreads } from '../../types/subjects';
import { useCreateEvent, useUpcomingEvents } from '../../hooks/useCalendar';
import { EVENT_TYPE_COLORS } from '../../types/calendar';

type RevisionStyle = 'quick' | 'deep' | 'practice';

const REVISION_STYLES: { id: RevisionStyle; label: string; desc: string; icon: React.ReactNode }[] = [
  { id: 'quick', label: 'Quick Review', desc: 'Key concepts and summaries', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'deep', label: 'Deep Dive', desc: 'Thorough explanations with examples', icon: <Brain className="h-4 w-4" /> },
  { id: 'practice', label: 'Practice Questions', desc: 'Exam-style questions with mark schemes', icon: <ListChecks className="h-4 w-4" /> },
];

interface RevisionPortalProps {
  subjectName: string | null;
  subjects: SubjectWithThreads[];
  onSubmit: (message: string) => void;
  onBack: () => void;
}

export const RevisionPortal: React.FC<RevisionPortalProps> = ({
  subjectName,
  subjects,
  onSubmit,
  onBack,
}) => {
  const [topic, setTopic] = useState('');
  const [examDate, setExamDate] = useState('');
  const [saveToCalendar, setSaveToCalendar] = useState(true);
  const [revisionStyle, setRevisionStyle] = useState<RevisionStyle>('quick');

  const createEvent = useCreateEvent();
  const { data: upcomingExams } = useUpcomingEvents(5);
  const exams = (upcomingExams || []).filter(e => e.event_type === 'exam' || e.event_type === 'test');

  const canSubmit = topic.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (examDate && saveToCalendar) {
      try {
        await createEvent.mutateAsync({
          title: `${subjectName ? subjectName + ' - ' : ''}${topic.trim()} Exam`,
          event_type: 'exam',
          event_date: new Date(examDate).toISOString(),
          subject_id: undefined,
        });
      } catch (err) {
        console.error('Failed to save calendar event:', err);
      }
    }

    const parts: string[] = [];
    const styleLabel = REVISION_STYLES.find(s => s.id === revisionStyle)?.label || 'Quick Review';
    parts.push(`Help me revise for my exam.`);
    if (subjectName) parts.push(`Subject: ${subjectName}`);
    parts.push(`Topic: ${topic.trim()}`);
    if (examDate) parts.push(`Exam date: ${examDate}`);
    parts.push(`Revision style: ${styleLabel}`);
    onSubmit(parts.join('\n'));
  };

  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <button
          onClick={onBack}
          className="mt-1 p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/20 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Revision / Exam Coaching</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Focused revision sessions to prepare for your exams
            </p>
          </div>
        </div>
      </div>

      {/* Subject Badge */}
      {subjectName && (
        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs font-medium text-blue-300">
            <Sparkles className="h-3 w-3" />
            {subjectName}
          </span>
        </div>
      )}

      <div className="space-y-6">
        {/* Topic */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            What topic are you revising? <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g., Photosynthesis, Quadratic Equations, The Cold War..."
            className="w-full px-4 py-3 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/50 focus:ring-2 focus:ring-[var(--accent-primary)]/10 text-sm"
          />
        </div>

        {/* Exam date + calendar checkbox */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Exam date <span className="text-[var(--text-tertiary)]">(optional)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              className="flex-1 px-4 py-3 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]/50 focus:ring-2 focus:ring-[var(--accent-primary)]/10 text-sm"
            />
          </div>
          {examDate && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saveToCalendar}
                onChange={(e) => setSaveToCalendar(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--border-color)] text-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/30"
              />
              <span className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                <CalendarPlus className="h-3.5 w-3.5" />
                Save to my calendar
              </span>
            </label>
          )}
        </div>

        {/* Revision style */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">
            Revision style
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {REVISION_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => setRevisionStyle(style.id)}
                className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border transition-all duration-200 text-left ${
                  revisionStyle === style.id
                    ? 'border-blue-500/50 bg-blue-500/10 shadow-sm'
                    : 'border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                <div className={`flex items-center gap-2 ${revisionStyle === style.id ? 'text-blue-400' : 'text-[var(--text-secondary)]'}`}>
                  {style.icon}
                  <span className="text-sm font-medium text-[var(--text-primary)]">{style.label}</span>
                </div>
                <span className="text-[10px] text-[var(--text-tertiary)] leading-tight">{style.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-medium text-sm transition-all duration-200 ${
            canSubmit
              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20'
              : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] cursor-not-allowed'
          }`}
        >
          <Send className="h-4 w-4" />
          Start Revision
        </button>
      </div>

      {/* Upcoming exams mini card */}
      {exams.length > 0 && (
        <div className="mt-8 p-4 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)]">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-blue-400" />
            Upcoming Exams
          </h3>
          <div className="space-y-2">
            {exams.slice(0, 3).map((exam) => (
              <div key={exam.id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: EVENT_TYPE_COLORS[exam.event_type as keyof typeof EVENT_TYPE_COLORS] || '#3b82f6' }}
                  />
                  <span className="text-[var(--text-primary)]">{exam.title}</span>
                </div>
                <span className="text-[var(--text-tertiary)]">
                  {new Date(exam.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
